import { useState, useEffect } from 'react';
import { ProductShowcase, cafeShowcaseItems } from './productShowCase';
import { CuratedCollections, cafeCollections } from './CuratedCollections';
import { Gallery, cafeSpaceProducts } from './Gallery';
import ScrollingSection from './ScrollingSection';
import ScrollToTop from './ScroollToTop';
import Cards from './Cards';
import Contact from './Contact';

const cafeCategories = ['Fine Dining', 'Cafe', 'Bistro', 'Outdoor'];

// Contact Modal Component
const ContactModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 h-[100vh] bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white shadow-3xl w-full max-w-2xl max-h-[90vh] sm:max-h-[95vh] overflow-y-auto relative rounded-lg">
        <button
          className="absolute top-3 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold z-[9999]"
          aria-label="Close modal"
          onClick={onClose}
        >
          &times;
        </button>
        <Contact />
      </div>
    </div>
  );
};

const Cafe = () => {
  const [showModal, setShowModal] = useState(false);

  // Preload hero image for faster display
  useEffect(() => {
    const img = new Image();
    img.src = "https://images.pexels.com/photos/1015568/pexels-photo-1015568.jpeg?auto=compress&cs=tinysrgb&w=1920";
  }, []);

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  return (
    <>
      <div className="font-serif animate-slideUp">
        <ScrollToTop />
        {/* Hero Section */}
        <header
          className="relative bg-cover bg-center h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-[80vh]"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/1015568/pexels-photo-1015568.jpeg?auto=compress&cs=tinysrgb&w=1920')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/10 to-transparent"></div>
          <div className="absolute inset-0 flex items-center px-4 sm:px-8 md:px-12 lg:px-16 z-10">
            <div className="w-full md:w-1/2 text-white">
              <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight">
                Crafting Spaces for Memorable Cafe
              </h1>
              <p className="font-heading mt-3 sm:mt-4 text-sm sm:text-base md:text-lg lg:text-xl">
                Elevate your cafe's atmosphere with our premium, handcrafted furniture collections designed for comfort and style.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-4 sm:mt-6">
                <button className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-6 sm:py-4 sm:px-8 text-sm sm:text-base rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg">
                  Explore the Collection
                </button>
                <button onClick={openModal} className="border-2 border-white hover:bg-white hover:text-gray-900 font-semibold py-3 px-6 sm:py-4 sm:px-8 text-sm sm:text-base rounded-lg transition-all duration-300 transform hover:scale-105">
                  Book Consultation
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Product Showcase Section */}
        <ProductShowcase
          title="Cafe Furniture Collections"
          items={cafeShowcaseItems}
        />


        {/* Signature Collection */}
        <section className="bg-gray-300 py-8 sm:py-10 md:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <h2 className="text-center text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold mb-6 sm:mb-8">Signature Collection</h2>
            <div className="bg-white p-4 sm:p-6 md:p-8 rounded-2xl shadow-md flex flex-col lg:flex-row items-center gap-6 lg:gap-8">
              <div className="w-full lg:w-1/3 relative overflow-hidden bg-gray-100 rounded-lg shadow-md">
                <div className="aspect-[4/5] w-full">
                  <img
                    src="/assets/caf_bst_3.jpg"
                    alt="The Artisan Chair"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { 
                      e.target.onerror = null; 
                      e.target.src = "https://placehold.co/600x750/E5E7EB/6B7280?text=Cafe+Design"; 
                    }}
                  />
                </div>
              </div>
              <div className="w-full">
                <h3 className="text-lg sm:text-xl md:text-2xl font-semibold">The Artisan Chair</h3>
                <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg leading-relaxed">
                  Our flagship dining chair combines traditional craftsmanship with modern design sensibilities. Handcrafted from sustainably sourced oak with premium upholstery,
                  it offers exceptional comfort for extended dining experiences while maintaining a sophisticated profile that elevates any restaurant interior.
                </p>
                <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                  <button className="bg-yellow-700 text-white py-2 sm:py-3 px-4 sm:px-6 text-sm sm:text-base rounded hover:bg-yellow-800 transition-colors">
                    View Details
                  </button>
                  <button onClick={openModal} className="bg-gray-700 text-white py-2 sm:py-3 px-4 sm:px-6 text-sm sm:text-base rounded hover:bg-gray-800 transition-colors">
                    Request a Quote
                  </button>
                </div>
                <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 text-sm sm:text-base">
                  <span className="text-gray-700 font-semibold">5+ Finishes</span>
                  <span className="text-gray-700 font-semibold">10-Year Warranty</span>
                  <span className="text-gray-700 font-semibold">100% Sustainable</span>
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* Gallery Section */}
      <section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16 min-h-[400px] sm:min-h-[600px] md:min-h-[800px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold mb-6 sm:mb-8 text-center">Cafe Gallery</h2>
          <div className="min-h-[300px] sm:min-h-[500px] md:min-h-[600px]">
            <Gallery
              products={cafeSpaceProducts}
              categories={cafeCategories}
              autoCycle={true}
            />
          </div>
        </div>
      </section>

      {/* Scrolling Section */}
      <section>
        <ScrollingSection
          sectionContent={
            <section className="relative z-10 bg-black/80 p-4 sm:p-6 md:p-8 lg:p-12 xl:p-16 w-full min-h-screen flex items-center justify-center">
              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
                  <div className="space-y-4 sm:space-y-6 text-left">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif text-white leading-tight">Elegant Cafe Setup</h2>
                    <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                      Transform your cafe into an inviting haven with our premium furniture collection. Featuring stylish designs and durable materials, each piece is crafted for comfort and ambiance, ensuring a welcoming and memorable experience for your guests.
                    </p>
                    <ul className="space-y-2 sm:space-y-3 text-white text-sm sm:text-base md:text-lg list-disc list-inside">
                      <li><strong>Seating:</strong> Comfortable chairs and booths with premium upholstery</li>
                      <li><strong>Tables:</strong> Sturdy cafe tables with elegant finishes</li>
                      <li><strong>Bar Stools:</strong> Modern design perfect for counter seating</li>
                      <li><strong>Lighting:</strong> Ambient fixtures creating a warm, inviting atmosphere</li>
                    </ul>
                    <button className="mt-6 sm:mt-8 inline-block bg-gray-600 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full text-sm sm:text-base md:text-lg font-semibold hover:bg-gray-700 transition-colors duration-300 shadow-lg hover:shadow-xl">
                      Explore More
                    </button>
                  </div>
                  <div>
                    <img
                      src="https://images.pexels.com/photos/302899/pexels-photo-302899.jpeg?auto=compress&cs=tinysrgb&w=800&q=80"
                      alt="Modern Cafe Interior"
                      className="rounded-xl shadow-lg w-full h-auto object-cover transform hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </div>
              </div>
            </section>
          }
          imageUrl="https://images.pexels.com/photos/1307698/pexels-photo-1307698.jpeg?auto=compress&cs=tinysrgb&w=1920"
        />
      </section>

      {/* Curated Collections */}
      <CuratedCollections
        title="Curated Cafe Collections"
        description="Discover cohesive furniture sets and decor ideas for your home's heart."
        collections={cafeCollections}
      />

      {/* Testimonials */}
      <Cards />

      {/* Contact Modal */}
      <ContactModal isOpen={showModal} onClose={closeModal} />
    </>
  );
};

export default Cafe;